
import Vue from "vue";
import Component from "vue-class-component";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import {
  MedicalCertificatesClient,
  OfficialMedicalCertificateLightViewModel,
  SortOrder,
} from "@/api-client";
import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
import { formatDate } from "@/formatting/dateFormatter";
import { BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
import ClientsidePagingSelector from "@/components/ClientsidePagingSelector.vue";
import Loader from "@/features/shared/components/Loader";
import ServersidePaging from "@/components/ServersidePaging";
import { debounceFn } from "debounce-decorator-ts";
import PopoverInfo from "@/components/PopoverInfo.vue";
import FilterCheckbox from "@/components/FilterCheckbox.vue";
import ValidityStatusBadge from "@/features/shared/components/ValidityStatusBadge.vue";

class Filters {
  searchTerm: string;
  showInvalid!: boolean;
  showValid!: boolean;
  showUnchecked!: boolean;
  sort?: string;
  sortDir?: SortOrder;

  constructor(
    searchTerm?: string,
    showInvalid?: boolean,
    showValid?: boolean,
    showUnchecked?: boolean,
    sort?: string,
    sortDir?: SortOrder
  ) {
    this.searchTerm = searchTerm || "";
    this.showInvalid = showInvalid || false;
    this.showValid = showValid || true;
    this.showUnchecked = showUnchecked || false;
    this.sort = sort;
    this.sortDir = sortDir ?? undefined;
  }
}

@Component({
  components: {
    PopoverInfo,
    TableEmptySlot,
    ValidityStatusBadge,
    ClientsidePagingSelector,
    ServersidePagingSelector,
    FilterCheckbox,
  },
})
export default class MedicalCertificatesTable extends Vue {
  SortOrder = SortOrder;
  medicalCertificates: OfficialMedicalCertificateLightViewModel[] = [];
  dataLoaded = false;
  filters = new Filters();
  paging = new ServersidePaging();
  loader = new Loader(true);

  async created() {
    this.getParametersFromRouteOrDefault();

    this.$watch(
      () => this.filters,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      },
      { deep: true }
    );

    this.$watch(
      () => this.paging.pageNumber,
      () => {
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.paging.pageSize,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.$route.query,
      () => {
        this.getMedicalCertificates();
      },
      { deep: true, immediate: true }
    );
  }

  navigateToDetails(
    medicalCertificate: OfficialMedicalCertificateLightViewModel
  ) {
    this.$router.push({
      name: "medical-certificate-details",
      params: {
        id: medicalCertificate.id,
        seafarerId: medicalCertificate.seafarerId,
      },
    });
  }

  async getMedicalCertificates() {
    this.medicalCertificates = [];
    const result = await new MedicalCertificatesClient()
      .setLoader(this.loader)
      .officialMedicalCertificates(
        this.filters.searchTerm,
        this.paging.pageNumber,
        this.paging.pageSize,
        this.filters.showValid,
        this.filters.showInvalid,
        this.filters.showUnchecked,
        this.filters.sortDir ?? null,
        this.filters.sort ?? ""
      );
    this.paging.totalCount = result.totalCount;
    this.paging.pageSize = result.pageSize;
    this.medicalCertificates = result.pageResult;
    this.dataLoaded = true;
  }

  @debounceFn(100) // Prevents possible double-update
  reflectParametersToRoute(): void {
    this.$router.replace({
      query: {
        // Setting to 'undefined' removes parameter from URL.
        searchTerm: this.filters.searchTerm || undefined,
        pageNumber: this.paging.pageNumber
          ? this.paging.pageNumber.toString()
          : undefined,
        pageSize: this.paging.pageSize
          ? this.paging.pageSize.toString()
          : undefined,
        showInvalid: this.filters.showInvalid.toString(),
        showValid: this.filters.showValid.toString(),
        showUnchecked: this.filters.showUnchecked.toString(),
        sort: this.filters?.sort ?? "",
        sortDir: this.filters?.sortDir ?? SortOrder.DESC,
      },
    });
  }

  updateSort(ctx: BvTableCtxObject) {
    // this.filters.sort = ctx.sortDesc ? ApplicationSortField.DateSubmittedDesc : ApplicationSortField.DateSubmittedAsc;
    this.filters.sort = ctx.sortBy ?? undefined;
    this.filters.sortDir = ctx.sortDesc ? SortOrder.DESC : SortOrder.ASC;
  }

  getParametersFromRouteOrDefault() {
    const route = this.$route as any;
    const searchTerm: string | undefined = route.query.searchTerm;
    const showInvalid: boolean | undefined = route.query.showInvalid;
    const showValid: boolean | undefined = route.query.showValid;
    const showUnchecked: boolean | undefined = route.query.showUnchecked;
    const sort: string | undefined = route.query.sort;
    const sortDir: SortOrder.DESC | SortOrder.ASC | undefined =
      route.query.sortDir;

    this.filters = new Filters(
      searchTerm,
      showInvalid,
      showValid,
      showUnchecked,
      sort,
      sortDir
    );

    this.paging.pageNumber =
      parseInt(route.query.pageNumber) || this.paging.pageNumber;
    this.paging.pageSize =
      parseInt(route.query.pageSize) || this.paging.pageSize;
  }

  fields: BvTableFieldArray = [
    {
      key: "documentNumber",
      label: this.$ls.Global_Label_DocumentNumber,
    },
    {
      key: "seafarerName",
      label: this.$ls.Global_Label_Seafarer,
    },
    {
      key: "birthDate",
      label: this.$ls.Global_Label_DateOfBirth,
      formatter: (
        value: any,
        key: string,
        item: OfficialMedicalCertificateLightViewModel
      ): string => formatDate(item.birthDate),
    },
    {
      key: "name",
      label: this.$ls.Global_Label_Name,
      thClass: "w-20",
    },
    {
      key: "issuedAt",
      label: this.$ls.Global_Label_IssueDateShort,
      formatter: (
        value: any,
        key: string,
        item: OfficialMedicalCertificateLightViewModel
      ): string => formatDate(item.issuedAt),
    },
    {
      key: "validUntil",
      label: this.$ls.Global_Label_ValidUntil,
      formatter: (
        value: any,
        key: string,
        item: OfficialMedicalCertificateLightViewModel
      ): string => formatDate(item.validUntil),
    },
    {
      key: "createdAt",
      label: this.$ls.Global_Created,
      sortable: true,
      formatter: (
        value: any,
        key: string,
        item: OfficialMedicalCertificateLightViewModel
      ): string => formatDate(item.createdAt),
    },
    {
      key: "statusString",
      label: this.$ls.Global_Label_DocumentState,
    },
    {
      key: "linkIcon",
      label: "",
      thClass: "w-5",
      tdClass: "table-actions",
    },
  ];
}
